<template>
  <div class="users-page">
    <PageContent class="users-page__content">
      <section v-if="hasAdvancedActions" class="users-metrics" aria-label="Suivi des campagnes email">
        <button
          type="button"
          class="users-metric-card users-metric-card--sent"
          :disabled="campaignsLoading"
          @click="openCampaignHistory"
        >
          <span class="users-metric-card__copy">
            <span>
              Relances envoyées
              <i class="far fa-clock" aria-hidden="true" />
            </span>
            <small>30 derniers jours · Voir l’historique</small>
          </span>
          <strong v-if="!campaignsLoading">{{ formatNumber(sentCampaigns.length) }}</strong>
          <i v-else class="fas fa-spinner fa-spin users-metric-card__loader" aria-label="Chargement" />
        </button>

        <article class="users-metric-card users-metric-card--scheduled">
          <span class="users-metric-card__copy">
            <span>Campagnes programmées</span>
            <small>Pour le domaine sélectionné</small>
          </span>
          <strong v-if="!campaignsLoading">{{ formatNumber(campaignSummary.scheduledCampaigns) }}</strong>
          <i v-else class="fas fa-spinner fa-spin users-metric-card__loader" aria-label="Chargement" />
        </article>

        <article class="users-metric-card users-metric-card--accounts">
          <span class="users-metric-card__copy">
            <span>Créations de compte</span>
            <small>Dans les 7 jours suivant l’envoi</small>
          </span>
          <strong v-if="!campaignsLoading">{{ formatNumber(campaignSummary.accountCreations) }}</strong>
          <i v-else class="fas fa-spinner fa-spin users-metric-card__loader" aria-label="Chargement" />
        </article>

        <button type="button" class="users-send-mail-button" @click="openBulkMailModal">
          <i class="fas fa-paper-plane" />
          <span>Envoyer une campagne mail</span>
        </button>
      </section>

      <section class="users-list-card">
        <header class="users-toolbar">
          <div class="users-search">
            <i class="fas fa-search" aria-hidden="true" />
            <input
              v-model="search"
              type="search"
              placeholder="Rechercher un utilisateur"
              aria-label="Rechercher un utilisateur"
              @input="scheduleSearch"
              @keyup.enter="applySearch"
            >
            <button
              v-if="search"
              type="button"
              aria-label="Effacer la recherche"
              @click="clearSearch"
            >
              <i class="fas fa-times" />
            </button>
          </div>

          <div class="users-toolbar__actions">
            <button
              v-if="canCreateUsers"
              type="button"
              class="users-add-button"
              @click="create"
            >
              <i class="fas fa-user-plus" />
              <span>Ajouter un utilisateur</span>
            </button>

            <template v-if="hasAdvancedActions">
              <b-tooltip label="Importer une liste d’utilisateurs" type="is-black" position="is-bottom">
                <button
                  type="button"
                  class="users-icon-button"
                  aria-label="Importer une liste d’utilisateurs"
                  @click="openImportModal"
                >
                  <i class="fas fa-file-import" />
                </button>
              </b-tooltip>

              <b-tooltip label="Ajouter un compte actif" type="is-black" position="is-bottom">
                <button
                  type="button"
                  class="users-icon-button"
                  aria-label="Ajouter un compte actif"
                  @click="openActivatedUserModal"
                >
                  <i class="fas fa-user-check" />
                </button>
              </b-tooltip>
            </template>

            <template v-if="canManageGroupsAndExport">
              <div class="users-action-tooltip users-action-tooltip--right">
                <button
                  type="button"
                  class="users-icon-button"
                  aria-label="Ouvrir la gestion des groupes d’utilisateurs"
                  aria-describedby="users-groups-tooltip"
                  @click="openGroupsModal"
                >
                  <i class="fas fa-users-cog" />
                </button>
                <span id="users-groups-tooltip" class="users-action-tooltip__content" role="tooltip">
                  Gérer les groupes d’utilisateurs
                </span>
              </div>

              <b-tooltip label="Exporter la liste filtrée" type="is-black" position="is-bottom">
                <button
                  type="button"
                  class="users-icon-button"
                  :disabled="exportingUsers"
                  aria-label="Exporter la liste"
                  @click="extractUsers"
                >
                  <i :class="exportingUsers ? 'fas fa-spinner fa-spin' : 'fas fa-file-export'" />
                </button>
              </b-tooltip>
            </template>
          </div>
        </header>

        <div class="users-table-scroll">
          <table
            class="users-table"
            :class="{
              'users-table--one-optional-column': hasInternalIds !== hasGroups,
              'users-table--two-optional-columns': hasInternalIds && hasGroups
            }"
          >
            <thead>
              <tr>
                <th class="users-column--selection">
                  <input
                    type="checkbox"
                    :checked="allPageUsersSelected"
                    :indeterminate.prop="somePageUsersSelected"
                    :disabled="!selectablePageUsers.length"
                    aria-label="Sélectionner tous les utilisateurs affichés"
                    @change="toggleAllPageUsers($event.target.checked)"
                  >
                </th>
                <th
                  class="users-column--role"
                  :class="{ 'users-column--menu-open': openColumnMenu === 'role' }"
                  :aria-sort="sortAria('management_role')"
                >
                  <div class="users-column-heading">
                    <button
                      id="users-column-trigger-role"
                      type="button"
                      class="users-column-trigger"
                      data-column-key="role"
                      :aria-expanded="openColumnMenu === 'role' ? 'true' : 'false'"
                      aria-controls="users-column-menu-role"
                      aria-haspopup="dialog"
                      @click.stop="toggleColumnMenu('role')"
                    >
                      <span>Rôle</span>
                      <i :class="columnMenuIcon('role', 'management_role')" aria-hidden="true" />
                      <span v-if="hasColumnFilter('role')" class="users-column-trigger__filter-dot">
                        <span class="sr-only">Filtre actif</span>
                      </span>
                    </button>

                    <div
                      v-if="openColumnMenu === 'role'"
                      id="users-column-menu-role"
                      class="users-column-popover"
                      role="dialog"
                      aria-label="Options de la colonne Rôle"
                      @click.stop
                    >
                      <div class="users-column-popover__group">
                        <span class="users-column-popover__label">Trier</span>
                        <div class="users-column-popover__actions">
                          <button
                            type="button"
                            :class="{ 'is-active': sortIsActive('management_role', 'asc') }"
                            @click="setSort('management_role', 'asc')"
                          >
                            Plus faible
                          </button>
                          <button
                            type="button"
                            :class="{ 'is-active': sortIsActive('management_role', 'desc') }"
                            @click="setSort('management_role', 'desc')"
                          >
                            Plus élevé
                          </button>
                        </div>
                      </div>

                      <label class="users-column-popover__group">
                        <span class="users-column-popover__label">Filtrer</span>
                        <select
                          :value="filters.role || ''"
                          aria-label="Filtrer par rôle"
                          @change="setColumnFilter('role', $event.target.value)"
                        >
                          <option value="">Tous</option>
                          <option v-for="role in roleFilterOptions" :key="role.value" :value="role.value">
                            {{ role.label }}
                          </option>
                        </select>
                      </label>

                      <button
                        v-if="hasColumnState('role', 'management_role')"
                        type="button"
                        class="users-column-popover__reset"
                        @click="clearColumnState('role', 'management_role')"
                      >
                        Réinitialiser
                      </button>
                    </div>
                  </div>
                </th>
                <th :aria-sort="sortAria('lastname')">
                  <button type="button" class="users-column-trigger" @click="setSort('lastname')">
                    <span>Nom</span>
                    <i :class="sortIcon('lastname')" aria-hidden="true" />
                  </button>
                </th>
                <th :aria-sort="sortAria('firstname')">
                  <button type="button" class="users-column-trigger" @click="setSort('firstname')">
                    <span>Prénom</span>
                    <i :class="sortIcon('firstname')" aria-hidden="true" />
                  </button>
                </th>
                <th class="users-column--email" :aria-sort="sortAria('email')">
                  <button type="button" class="users-column-trigger" @click="setSort('email')">
                    <span>Adresse e-mail</span>
                    <i :class="sortIcon('email')" aria-hidden="true" />
                  </button>
                </th>
                <th v-if="hasInternalIds" :aria-sort="sortAria('internal_id_number')">
                  <button type="button" class="users-column-trigger" @click="setSort('internal_id_number')">
                    <span>Matricule</span>
                    <i :class="sortIcon('internal_id_number')" aria-hidden="true" />
                  </button>
                </th>
                <th
                  v-if="hasGroups"
                  class="users-column--groups"
                  :class="{ 'users-column--menu-open': openColumnMenu === 'group' }"
                  :aria-sort="sortAria('management_group')"
                >
                  <div class="users-column-heading">
                    <button
                      id="users-column-trigger-group"
                      type="button"
                      class="users-column-trigger"
                      data-column-key="group"
                      :aria-expanded="openColumnMenu === 'group' ? 'true' : 'false'"
                      aria-controls="users-column-menu-group"
                      aria-haspopup="dialog"
                      @click.stop="toggleColumnMenu('group')"
                    >
                      <span>Groupe</span>
                      <i :class="columnMenuIcon('group', 'management_group')" aria-hidden="true" />
                      <span v-if="hasColumnFilter('group')" class="users-column-trigger__filter-dot">
                        <span class="sr-only">Filtre actif</span>
                      </span>
                    </button>

                    <div
                      v-if="openColumnMenu === 'group'"
                      id="users-column-menu-group"
                      class="users-column-popover"
                      role="dialog"
                      aria-label="Options de la colonne Groupe"
                      @click.stop
                    >
                      <div class="users-column-popover__group">
                        <span class="users-column-popover__label">Trier</span>
                        <div class="users-column-popover__actions">
                          <button
                            type="button"
                            :class="{ 'is-active': sortIsActive('management_group', 'asc') }"
                            @click="setSort('management_group', 'asc')"
                          >
                            A → Z
                          </button>
                          <button
                            type="button"
                            :class="{ 'is-active': sortIsActive('management_group', 'desc') }"
                            @click="setSort('management_group', 'desc')"
                          >
                            Z → A
                          </button>
                        </div>
                      </div>

                      <label class="users-column-popover__group">
                        <span class="users-column-popover__label">Filtrer</span>
                        <select
                          :value="filters.group || ''"
                          aria-label="Filtrer par groupe"
                          @change="setColumnFilter('group', $event.target.value)"
                        >
                          <option value="">Tous</option>
                          <option v-for="group in groupFilterOptions" :key="group.value" :value="group.value">
                            {{ group.label }}
                          </option>
                        </select>
                      </label>

                      <button
                        v-if="hasColumnState('group', 'management_group')"
                        type="button"
                        class="users-column-popover__reset"
                        @click="clearColumnState('group', 'management_group')"
                      >
                        Réinitialiser
                      </button>
                    </div>
                  </div>
                </th>
                <th class="users-column--date" :aria-sort="sortAria('email_verified_at')">
                  <button type="button" class="users-column-trigger" @click="setSort('email_verified_at')">
                    <span>Date inscription</span>
                    <i :class="sortIcon('email_verified_at')" aria-hidden="true" />
                  </button>
                </th>
                <th
                  class="users-column--status"
                  :class="{ 'users-column--menu-open': openColumnMenu === 'status' }"
                  :aria-sort="sortAria('is_verified')"
                >
                  <div class="users-column-heading">
                    <button
                      id="users-column-trigger-status"
                      type="button"
                      class="users-column-trigger"
                      data-column-key="status"
                      :aria-expanded="openColumnMenu === 'status' ? 'true' : 'false'"
                      aria-controls="users-column-menu-status"
                      aria-haspopup="dialog"
                      @click.stop="toggleColumnMenu('status')"
                    >
                      <span>Statut</span>
                      <i :class="columnMenuIcon('status', 'is_verified')" aria-hidden="true" />
                      <span v-if="hasColumnFilter('status')" class="users-column-trigger__filter-dot">
                        <span class="sr-only">Filtre actif</span>
                      </span>
                    </button>

                    <div
                      v-if="openColumnMenu === 'status'"
                      id="users-column-menu-status"
                      class="users-column-popover"
                      role="dialog"
                      aria-label="Options de la colonne Statut"
                      @click.stop
                    >
                      <div class="users-column-popover__group">
                        <span class="users-column-popover__label">Trier</span>
                        <div class="users-column-popover__actions">
                          <button
                            type="button"
                            :class="{ 'is-active': sortIsActive('is_verified', 'asc') }"
                            @click="setSort('is_verified', 'asc')"
                          >
                            Non téléchargés
                          </button>
                          <button
                            type="button"
                            :class="{ 'is-active': sortIsActive('is_verified', 'desc') }"
                            @click="setSort('is_verified', 'desc')"
                          >
                            Téléchargés
                          </button>
                        </div>
                      </div>

                      <label class="users-column-popover__group">
                        <span class="users-column-popover__label">Filtrer</span>
                        <select
                          :value="filters.status || ''"
                          aria-label="Filtrer par statut"
                          @change="setColumnFilter('status', $event.target.value)"
                        >
                          <option value="">Tous</option>
                          <option value="downloaded">Téléchargé</option>
                          <option value="not_downloaded">Non téléchargé</option>
                        </select>
                      </label>

                      <button
                        v-if="hasColumnState('status', 'is_verified')"
                        type="button"
                        class="users-column-popover__reset"
                        @click="clearColumnState('status', 'is_verified')"
                      >
                        Réinitialiser
                      </button>
                    </div>
                  </div>
                </th>
              </tr>
            </thead>

            <tbody>
              <tr v-if="usersLoading">
                <td :colspan="visibleColumnCount" class="users-table-state">
                  <i class="fas fa-spinner fa-spin" />
                  <span>Chargement des utilisateurs…</span>
                </td>
              </tr>
              <tr v-else-if="usersError">
                <td :colspan="visibleColumnCount" class="users-table-state users-table-state--error">
                  <span>{{ usersError }}</span>
                  <button type="button" @click="loadUsers">Réessayer</button>
                </td>
              </tr>
              <tr v-else-if="!users.length">
                <td :colspan="visibleColumnCount" class="users-table-state">
                  Aucun utilisateur ne correspond à cette recherche.
                </td>
              </tr>
              <template v-else>
                <tr
                  v-for="user in users"
                  :key="user.uuid"
                  :class="{ 'users-row--editable': canOpenUser(user) }"
                  :tabindex="canOpenUser(user) ? 0 : null"
                  @click="openModal({ data: user })"
                  @keydown.enter.self="openModal({ data: user })"
                >
                <td class="users-column--selection" @click.stop>
                  <input
                    type="checkbox"
                    :checked="userIsSelected(user)"
                    :disabled="!canSelectUser(user)"
                    :aria-label="`Sélectionner ${formatUserIdentifier(user)}`"
                    @change="toggleUserSelection(user, $event.target.checked)"
                  >
                </td>
                <td class="users-column--role">
                  <span class="users-role" :class="`users-role--${user.management_role}`">
                    {{ roleLabel(user.management_role) }}
                  </span>
                </td>
                <td>{{ displayValue(user.lastname) }}</td>
                <td>{{ displayValue(user.firstname) }}</td>
                <td class="users-column--email" :title="user.email || ''">
                  {{ displayValue(user.email) }}
                </td>
                <td v-if="hasInternalIds">{{ displayValue(user.internal_id_number) }}</td>
                <td v-if="hasGroups" class="users-column--groups" :title="groupLabels(user)">
                  {{ groupLabels(user) }}
                </td>
                <td class="users-column--date">{{ formatRegistrationDate(user.email_verified_at) }}</td>
                <td class="users-column--status">
                  <span
                    class="users-status"
                    :class="isDownloaded(user) ? 'users-status--downloaded' : 'users-status--not-downloaded'"
                    :title="isDownloaded(user) ? 'Téléchargé' : 'Non téléchargé'"
                    :aria-label="isDownloaded(user) ? 'Téléchargé' : 'Non téléchargé'"
                  >
                    <i :class="isDownloaded(user) ? 'fas fa-check-circle' : 'fas fa-times-circle'" />
                  </span>
                </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>

        <footer class="users-table-footer">
          <div class="users-table-footer__selection">
            <button
              type="button"
              class="users-delete-button"
              :disabled="selectedUsers.length === 0 || deletingUsers"
              :title="selectedUsers.length ? 'Supprimer la sélection' : 'Sélectionnez des utilisateurs à supprimer'"
              aria-label="Supprimer la sélection"
              @click="confirmDeleteUsers"
            >
              <i :class="deletingUsers ? 'fas fa-spinner fa-spin' : 'fas fa-trash-alt'" />
            </button>
            <span v-if="selectedUsers.length" class="users-selection-count">
              {{ selectedUsers.length }} sélectionné{{ selectedUsers.length > 1 ? 's' : '' }}
            </span>
          </div>

          <div class="users-pagination">
            <span>{{ paginationLabel }}</span>
            <template v-if="pageCount > 1">
              <button
                type="button"
                :disabled="currentPage === 1 || usersLoading"
                aria-label="Page précédente"
                @click="goToPage(currentPage - 1)"
              >
                <i class="fas fa-chevron-left" />
              </button>
              <span>Page {{ currentPage }} / {{ pageCount }}</span>
              <button
                type="button"
                :disabled="currentPage === pageCount || usersLoading"
                aria-label="Page suivante"
                @click="goToPage(currentPage + 1)"
              >
                <i class="fas fa-chevron-right" />
              </button>
            </template>
          </div>
        </footer>
      </section>
    </PageContent>

    <t-modal name="user-form" variant="contentForm">
      <MVPUsersForm
        v-if="currentUser"
        :user="currentUser"
        :userKey="currentUser.uuid"
        :role="currentUser.management_role"
        :groups="groups"
        :domains="domains"
        @refresh="loadUsers"
      />
    </t-modal>

  </div>
</template>

<script>
import { http } from '@/services/api.service'
import PageContent from '@/components/PageContent.vue'
import CreateUser from './Modals/Create.vue'
import CreateActivatedUser from './Modals/CreateActivated.vue'
import MVPUsersForm from './Form.vue'
import UserCsvImportModal from './Import.vue'

const ROLE_LABELS = {
  user: 'Utilisateur',
  operator: 'Observateur',
  director: 'Administrateur',
  'community-manager': 'Community manager',
  admin: 'Équipe interne'
}

export default {
  name: 'app-users-list',
  components: {
    PageContent,
    MVPUsersForm
  },
  data () {
    return {
      groups: [],
      domains: [],
      currentUser: null,
      users: [],
      usersLoading: false,
      usersError: '',
      usersRequestId: 0,
      selectedUsers: [],
      totalRowCount: 0,
      currentPage: 1,
      pageSize: 100,
      search: '',
      searchTimer: null,
      currentSortModel: [
        { colId: 'management_role', sort: 'desc' },
        { colId: 'lastname', sort: 'asc' }
      ],
      filters: {
        role: '',
        group: '',
        status: ''
      },
      openColumnMenu: null,
      hasInternalIds: false,
      hasGroups: false,
      campaignsLoading: false,
      campaignRows: [],
      scheduledCampaignRows: [],
      campaignSummary: {
        scheduledCampaigns: 0,
        accountCreations: 0
      },
      deletingUsers: false,
      exportingUsers: false
    }
  },
  computed: {
    domain () {
      return this.$store.getters.domain || {}
    },
    currentAccount () {
      return this.$store.getters.user || {}
    },
    normalizedRoles () {
      const roles = this.$store.getters.roles || []
      if (!Array.isArray(roles)) return []

      return roles
        .map(role => {
          if (typeof role === 'string') return role.toLowerCase()
          if (role && typeof role.name === 'string') return role.name.toLowerCase()
          return ''
        })
        .filter(Boolean)
    },
    hasAdvancedActions () {
      return this.normalizedRoles.some(role => ['admin', 'community-manager'].includes(role))
    },
    canManageGroupsAndExport () {
      return this.normalizedRoles.some(role => ['admin', 'director', 'community-manager'].includes(role))
    },
    canCreateUsers () {
      return this.normalizedRoles.some(role => ['admin', 'director', 'community-manager'].includes(role))
    },
    createRoleOptions () {
      let roles = []

      if (this.normalizedRoles.includes('admin') || this.normalizedRoles.includes('community-manager')) {
        roles = ['user', 'operator', 'director', 'community-manager']
      } else if (this.normalizedRoles.includes('director')) {
        roles = ['user', 'operator']
      }

      return roles.map(role => ({ value: role, label: ROLE_LABELS[role] }))
    },
    roleFilterOptions () {
      const roles = this.hasAdvancedActions
        ? ['user', 'operator', 'director']
        : ['user', 'operator']

      return roles.map(role => ({ value: role, label: ROLE_LABELS[role] }))
    },
    groupFilterOptions () {
      const options = this.groups
        .filter(group => group && group.uuid)
        .map(group => ({ value: group.uuid, label: group.label || 'Groupe sans nom' }))
        .sort((left, right) => left.label.localeCompare(right.label, 'fr', { sensitivity: 'base' }))

      return [{ value: 'none', label: 'Sans groupe' }, ...options]
    },
    sentCampaigns () {
      // Every campaign sent in the period, whatever its template: a custom mail is a
      // reminder too from the client's point of view.
      return this.campaignRows
    },
    visibleColumnCount () {
      return 7 + (this.hasInternalIds ? 1 : 0) + (this.hasGroups ? 1 : 0)
    },
    selectablePageUsers () {
      return this.users.filter(user => this.canSelectUser(user))
    },
    allPageUsersSelected () {
      return Boolean(
        this.selectablePageUsers.length &&
        this.selectablePageUsers.every(user => this.userIsSelected(user))
      )
    },
    somePageUsersSelected () {
      return !this.allPageUsersSelected && this.selectablePageUsers.some(user => this.userIsSelected(user))
    },
    pageCount () {
      return Math.max(1, Math.ceil(this.totalRowCount / this.pageSize))
    },
    paginationLabel () {
      if (!this.totalRowCount) return '0 utilisateur'
      const first = ((this.currentPage - 1) * this.pageSize) + 1
      const last = Math.min(this.currentPage * this.pageSize, this.totalRowCount)
      return `${first}–${last} sur ${this.formatNumber(this.totalRowCount)}`
    }
  },
  watch: {
    hasAdvancedActions (value, oldValue) {
      if (value && !oldValue) {
        this.loadCampaigns()
      }
    },
    domain: {
      deep: true,
      handler (newDomain, oldDomain) {
        if (newDomain?.uuid && newDomain.uuid !== oldDomain?.uuid) {
          this.currentPage = 1
          this.selectedUsers = []
          this.filters = { role: '', group: '', status: '' }
          this.openColumnMenu = null
          this.loadGroups()
          this.loadDomains()
          this.loadCampaigns()
          this.loadUsers()
        }
      }
    }
  },
  created () {
    this.loadGroups()
    this.loadDomains()
    this.loadUsers()
    if (this.hasAdvancedActions) {
      this.loadCampaigns()
    }
  },
  mounted () {
    document.addEventListener('click', this.closeColumnMenu)
    document.addEventListener('keydown', this.handleColumnMenuKeydown)
  },
  beforeDestroy () {
    window.clearTimeout(this.searchTimer)
    document.removeEventListener('click', this.closeColumnMenu)
    document.removeEventListener('keydown', this.handleColumnMenuKeydown)
  },
  methods: {
    scheduleSearch () {
      window.clearTimeout(this.searchTimer)
      this.searchTimer = window.setTimeout(this.applySearch, 300)
    },
    applySearch () {
      window.clearTimeout(this.searchTimer)
      this.currentPage = 1
      this.loadUsers()
    },
    clearSearch () {
      this.search = ''
      this.applySearch()
    },
    async loadUsers () {
      if (!this.domain?.uuid) return

      const requestId = ++this.usersRequestId
      const startRow = (this.currentPage - 1) * this.pageSize
      this.usersLoading = true
      this.usersError = ''

      try {
        const { data } = await http.get('/user/management', {
          params: {
            startRow,
            endRow: startRow + this.pageSize,
            search: this.search.trim(),
            role: this.filters.role || undefined,
            group: this.filters.group || undefined,
            status: this.filters.status || undefined,
            sortModel: JSON.stringify(this.currentSortModel)
          }
        })

        if (requestId !== this.usersRequestId) return

        this.users = Array.isArray(data?.rows) ? data.rows : []
        this.totalRowCount = Number(data?.totalCount || 0)
        this.hasInternalIds = Boolean(data?.meta?.hasInternalIds)
        this.hasGroups = Boolean(data?.meta?.hasGroups)

        const lastPage = Math.max(1, Math.ceil(this.totalRowCount / this.pageSize))
        if (this.currentPage > lastPage) {
          this.currentPage = lastPage
          this.loadUsers()
        }
      } catch (error) {
        if (requestId !== this.usersRequestId) return
        this.users = []
        this.totalRowCount = 0
        this.usersError = 'Impossible de récupérer les utilisateurs pour le moment.'
      } finally {
        if (requestId === this.usersRequestId) {
          this.usersLoading = false
        }
      }
    },
    toggleColumnMenu (column) {
      this.openColumnMenu = this.openColumnMenu === column ? null : column
    },
    closeColumnMenu () {
      this.openColumnMenu = null
    },
    handleColumnMenuKeydown (event) {
      if (event.key !== 'Escape' || !this.openColumnMenu) return
      const column = this.openColumnMenu
      this.closeColumnMenu()
      this.$nextTick(() => {
        const trigger = this.$el.querySelector(`[data-column-key="${column}"]`)
        if (trigger) trigger.focus()
      })
    },
    setColumnFilter (column, value) {
      this.$set(this.filters, column, value)
      this.currentPage = 1
      this.loadUsers()
    },
    hasColumnFilter (column) {
      return Boolean(this.filters[column])
    },
    hasColumnState (filterColumn, sortColumn) {
      return this.hasColumnFilter(filterColumn) || Boolean(
        sortColumn && this.currentSortModel.some(sort => sort.colId === sortColumn)
      )
    },
    clearColumnState (filterColumn, sortColumn) {
      this.$set(this.filters, filterColumn, '')
      if (sortColumn) {
        this.currentSortModel = this.currentSortModel.filter(sort => sort.colId !== sortColumn)
      }
      this.currentPage = 1
      this.loadUsers()
    },
    setSort (column, requestedDirection = null) {
      const current = this.currentSortModel.find(sort => sort.colId === column) || {}
      const defaultDirection = column === 'management_role' ? 'desc' : 'asc'
      const direction = requestedDirection || (current.sort
        ? (current.sort === 'asc' ? 'desc' : 'asc')
        : defaultDirection)
      this.currentSortModel = column === 'management_role'
        ? [
          { colId: column, sort: direction },
          { colId: 'lastname', sort: 'asc' }
        ]
        : [{ colId: column, sort: direction }]
      this.currentPage = 1
      this.loadUsers()
    },
    sortIsActive (column, direction) {
      return this.currentSortModel.some(sort => sort.colId === column && sort.sort === direction)
    },
    columnMenuIcon (filterColumn, sortColumn = null) {
      const current = sortColumn && this.currentSortModel.find(sort => sort.colId === sortColumn)
      if (current) return current.sort === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down'
      if (this.hasColumnFilter(filterColumn)) return 'fas fa-filter'
      return 'fas fa-chevron-down'
    },
    sortIcon (column) {
      const current = this.currentSortModel.find(sort => sort.colId === column)
      if (!current) return 'fas fa-chevron-down'
      return current.sort === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down'
    },
    sortAria (column) {
      const current = this.currentSortModel.find(sort => sort.colId === column)
      if (!current) return 'none'
      return current.sort === 'asc' ? 'ascending' : 'descending'
    },
    goToPage (page) {
      if (page < 1 || page > this.pageCount || page === this.currentPage) return
      this.currentPage = page
      this.loadUsers()
    },
    toggleUserSelection (user, selected) {
      if (!this.canSelectUser(user)) return
      if (selected && !this.userIsSelected(user)) {
        this.selectedUsers = [...this.selectedUsers, user]
      } else if (!selected) {
        this.selectedUsers = this.selectedUsers.filter(item => item.uuid !== user.uuid)
      }
    },
    toggleAllPageUsers (selected) {
      if (selected) {
        const selectedUuids = new Set(this.selectedUsers.map(user => user.uuid))
        this.selectedUsers = [
          ...this.selectedUsers,
          ...this.selectablePageUsers.filter(user => !selectedUuids.has(user.uuid))
        ]
        return
      }

      const pageUuids = new Set(this.selectablePageUsers.map(user => user.uuid))
      this.selectedUsers = this.selectedUsers.filter(user => !pageUuids.has(user.uuid))
    },
    userIsSelected (user) {
      return this.selectedUsers.some(item => item.uuid === user.uuid)
    },
    canOpenUser (user) {
      return Boolean(user && !['admin', 'community-manager'].includes(user.management_role))
    },
    roleLabel (role) {
      return ROLE_LABELS[role] || 'Utilisateur'
    },
    displayValue (value) {
      return value === null || value === undefined || String(value).trim() === '' ? '—' : value
    },
    groupLabels (user) {
      const labels = (Array.isArray(user?.groups) ? user.groups : [])
        .map(group => group.label)
        .filter(Boolean)
      return labels.join(', ') || '—'
    },
    formatRegistrationDate (value) {
      if (!value) return '—'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return '—'
      const day = new Intl.DateTimeFormat('fr-FR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      }).format(date)
      const time = new Intl.DateTimeFormat('fr-FR', {
        hour: '2-digit',
        minute: '2-digit'
      }).format(date)
      return `${day} - ${time}`
    },
    isDownloaded (user) {
      return [true, 1, '1', 'true'].includes(user?.is_verified)
    },
    loadGroups () {
      return http.get('/groups')
        .then(({ data }) => {
          this.groups = Array.isArray(data) ? data : []
        })
        .catch(() => {
          this.groups = []
        })
    },
    loadDomains () {
      return http.get('/domain')
        .then(({ data }) => {
          const domains = Array.isArray(data) ? data : []
          this.domains = domains.filter(domain => domain?.application?.slug !== 'disabled')
        })
        .catch(() => {
          this.domains = []
        })
    },
    loadCampaigns () {
      if (!this.hasAdvancedActions || !this.domain?.uuid) return
      this.campaignsLoading = true

      http.get('/dashboard/email-campaigns', {
        params: { domains: [this.domain.uuid] }
      })
        .then(({ data }) => {
          const campaigns = Array.isArray(data?.campaigns) ? data.campaigns : []
          const scheduled = Array.isArray(data?.scheduledCampaigns) ? data.scheduledCampaigns : []
          const summary = data?.summary || {}
          this.campaignRows = campaigns
          this.scheduledCampaignRows = scheduled
          this.campaignSummary = {
            scheduledCampaigns: Number(summary.scheduledCampaigns || scheduled.length || 0),
            accountCreations: Number(summary.accountCreations || 0)
          }
        })
        .catch(() => {
          this.campaignRows = []
          this.scheduledCampaignRows = []
          this.campaignSummary = { scheduledCampaigns: 0, accountCreations: 0 }
        })
        .finally(() => {
          this.campaignsLoading = false
        })
    },
    create () {
      this.$buefy.modal.open({
        parent: this,
        component: CreateUser,
        hasModalCard: true,
        destroyOnHide: true,
        trapFocus: true,
        ariaRole: 'dialog',
        ariaModal: true,
        props: {
          roleOptions: this.createRoleOptions
        },
        events: {
          created: created => {
            this.loadUsers()
            this.notifySuccess(`Le compte ${this.formatUserIdentifier(created)} a été créé.`)
          }
        }
      })
    },
    openActivatedUserModal () {
      this.$buefy.modal.open({
        parent: this,
        component: CreateActivatedUser,
        hasModalCard: true,
        destroyOnHide: true,
        trapFocus: true,
        ariaRole: 'dialog',
        ariaModal: true,
        events: {
          created: created => {
            this.loadUsers()
            this.notifySuccess(`Le compte ${this.formatUserIdentifier(created)} a été créé et activé.`)
          }
        }
      })
    },
    openImportModal () {
      this.$buefy.modal.open({
        parent: this,
        component: UserCsvImportModal,
        hasModalCard: true,
        destroyOnHide: true,
        trapFocus: true,
        ariaRole: 'dialog',
        ariaModal: true,
        events: {
          imported: this.loadUsers
        }
      })
    },
    async openBulkMailModal () {
      const { default: UserBulkMailModal } = await import('./Modals/BulkMail.vue')
      this.$buefy.modal.open({
        parent: this,
        component: UserBulkMailModal,
        hasModalCard: true,
        trapFocus: true,
        events: {
          sent: this.loadCampaigns
        }
      })
    },
    async openCampaignHistory () {
      const { default: CampaignHistoryModal } = await import('./Modals/CampaignHistory.vue')
      this.$buefy.modal.open({
        parent: this,
        component: CampaignHistoryModal,
        hasModalCard: true,
        width: 1120,
        trapFocus: true,
        props: {
          campaigns: this.sentCampaigns
        }
      })
    },
    async openGroupsModal () {
      const { default: UserGroupsModal } = await import('./Modals/Groups.vue')
      this.$buefy.modal.open({
        parent: this,
        component: UserGroupsModal,
        hasModalCard: true,
        canCancel: ['escape', 'outside'],
        trapFocus: true,
        events: {
          'groups-updated': () => {
            this.loadGroups()
            this.loadUsers()
          }
        }
      })
    },
    openModal ({ data }) {
      if (!data || ['admin', 'community-manager'].includes(data.management_role)) return
      this.currentUser = data
      this.$modal.show('user-form', data)
    },
    async extractUsers () {
      if (this.exportingUsers) return
      this.exportingUsers = true
      const params = {
        search: this.search.trim(),
        role: this.filters.role || undefined,
        group: this.filters.group || undefined,
        status: this.filters.status || undefined,
        sortModel: JSON.stringify(this.currentSortModel || [])
      }

      try {
        const { data } = await http.get('/user/management/export', { params, responseType: 'blob' })
        const url = window.URL.createObjectURL(new Blob([data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', 'utilisateurs.csv')
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      } catch (error) {
        this.notifyError('Impossible d’exporter la liste des utilisateurs.')
      } finally {
        this.exportingUsers = false
      }
    },
    confirmDeleteUsers () {
      const count = this.selectedUsers.length
      if (!count) return

      this.$buefy.dialog.confirm({
        title: `Supprimer ${count} utilisateur${count > 1 ? 's' : ''} ?`,
        message: `
          <p>Les comptes sélectionnés seront retirés du domaine.</p>
          ${this.deleteUsersSummary()}
        `,
        cancelText: 'Annuler',
        confirmText: 'Continuer',
        type: 'is-danger',
        hasIcon: true,
        onConfirm: () => this.confirmDeleteUsersAgain()
      })
    },
    confirmDeleteUsersAgain () {
      this.$buefy.dialog.confirm({
        title: 'Confirmation définitive',
        message: 'Cette action est irréversible pour les comptes qui ne sont rattachés à aucun autre domaine.',
        cancelText: 'Annuler',
        confirmText: 'Supprimer définitivement',
        type: 'is-danger',
        hasIcon: true,
        onConfirm: () => this.deleteUsers()
      })
    },
    async deleteUsers () {
      if (this.deletingUsers) return
      this.deletingUsers = true
      let deleted = 0
      let failed = 0
      const failedUsers = []
      const usersToDelete = [...this.selectedUsers]

      for (const user of usersToDelete) {
        try {
          const endpoint = user.management_role === 'community-manager'
            ? `/community-manager/${user.uuid}`
            : `/user/${user.uuid}`
          await http.delete(endpoint)
          deleted += 1
        } catch (error) {
          failed += 1
          failedUsers.push(user)
        }
      }

      this.deletingUsers = false
      this.selectedUsers = failedUsers
      this.loadUsers()
      if (deleted) {
        this.notifySuccess(`${deleted} compte${deleted > 1 ? 's ont' : ' a'} été supprimé${deleted > 1 ? 's' : ''}.`)
      }
      if (failed) {
        this.notifyError(`${failed} compte${failed > 1 ? 's n’ont' : ' n’a'} pas pu être supprimé${failed > 1 ? 's' : ''}.`)
      }
    },
    canSelectUser (user) {
      if (!user) return false
      if (user.management_role === 'admin') return false
      return user.uuid !== this.currentAccount.uuid
    },
    formatUserIdentifier (user) {
      return user?.email || user?.internal_id_number || 'sans identifiant'
    },
    deleteUsersSummary () {
      const rows = this.selectedUsers.map(user => `
        <tr>
          <td style="padding: 7px 9px; border-top: 1px solid #e5e7eb;">${this.escapeHtml(this.displayValue(user.lastname))}</td>
          <td style="padding: 7px 9px; border-top: 1px solid #e5e7eb;">${this.escapeHtml(this.displayValue(user.firstname))}</td>
          <td style="padding: 7px 9px; border-top: 1px solid #e5e7eb; word-break: break-word;">${this.escapeHtml(this.displayValue(user.email))}</td>
        </tr>
      `).join('')

      return `
        <div style="max-height: 240px; margin-top: 12px; overflow: auto; border: 1px solid #e5e7eb; border-radius: 8px; text-align: left;">
          <table style="width: 100%; border-collapse: collapse; font-size: 12px;">
            <thead style="position: sticky; top: 0; background: #f9fafb; color: #475569;">
              <tr>
                <th style="padding: 7px 9px;">Nom</th>
                <th style="padding: 7px 9px;">Prénom</th>
                <th style="padding: 7px 9px;">Adresse e-mail</th>
              </tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>
        </div>
      `
    },
    escapeHtml (value) {
      return String(value ?? '').replace(/[&<>'"]/g, character => ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        "'": '&#039;',
        '"': '&quot;'
      }[character]))
    },
    formatNumber (value) {
      return new Intl.NumberFormat('fr-FR').format(Number(value || 0))
    },
    notifySuccess (message) {
      this.$notify({ group: 'maviepro-success', text: message })
    },
    notifyError (message) {
      this.$notify({ group: 'maviepro-error', text: message })
    }
  }
}
</script>

<style scoped>
.users-page {
  height: calc(100vh - 64px);
  background: #f3f4f6;
  overflow: hidden;
}

.users-page__content {
  display: flex;
  height: calc(100% - 40px);
  min-height: 0;
  flex-direction: column;
}

.users-metrics {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr)) minmax(13rem, 0.68fr);
  flex: 0 0 auto;
  gap: 20px;
  margin-bottom: 24px;
}

.users-metric-card,
.users-send-mail-button {
  min-height: 88px;
  border-radius: 14px;
}

.users-metric-card {
  position: relative;
  display: flex;
  overflow: hidden;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 18px 24px;
  border: 1px solid #e5e7eb;
  background: #fff;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.06);
  text-align: left;
  transition: border-color 150ms ease, box-shadow 150ms ease, transform 150ms ease;
}

.users-metric-card--sent {
  background: linear-gradient(180deg, #eff6ff 0%, #fff 62%);
  cursor: pointer;
}

.users-metric-card--sent:disabled {
  cursor: wait;
}

.users-metric-card--scheduled {
  background: linear-gradient(180deg, #f0fdfa 0%, #fff 62%);
}

.users-metric-card--accounts {
  background: linear-gradient(180deg, #ecfdf5 0%, #fff 62%);
}

.users-metric-card__copy {
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
}

.users-metric-card__copy > span {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  color: #111827;
  font-size: 1.125rem;
  font-weight: 700;
}

.users-metric-card strong {
  flex: 0 0 auto;
  color: #111827;
  font-size: 1.25rem;
  line-height: 1;
}

.users-metric-card__copy small {
  display: block;
  color: #94a3b8;
  font-size: 0.75rem;
  line-height: 1.3;
}

.users-metric-card--sent:hover:not(:disabled) {
  border-color: #93c5fd;
  box-shadow: 0 8px 20px rgba(37, 99, 235, 0.1);
  transform: translateY(-1px);
}

.users-metric-card__loader {
  flex: 0 0 auto;
  color: #64748b;
  font-size: 1.25rem;
}

.users-send-mail-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 14px 18px;
  background: #2563eb;
  box-shadow: 0 8px 18px rgba(37, 99, 235, 0.2);
  color: #fff;
  font-size: 0.875rem;
  font-weight: 400;
  transition: background 150ms ease, box-shadow 150ms ease, transform 150ms ease;
}

.users-send-mail-button:hover {
  background: #1d4ed8;
  transform: translateY(-1px);
}

.users-send-mail-button:focus-visible,
.users-metric-card--sent:focus-visible,
.users-add-button:focus-visible,
.users-icon-button:focus-visible,
.users-delete-button:focus-visible,
.users-search button:focus-visible {
  outline: 2px solid #2563eb;
  outline-offset: 3px;
}

.users-list-card {
  display: flex;
  min-height: 0;
  flex: 1 1 auto;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid #bfdbfe;
  border-radius: 14px;
  background: #fff;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.06);
}

.users-toolbar {
  position: relative;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  flex: 0 0 auto;
  gap: 18px 24px;
  padding: 22px 28px;
}

.users-search {
  display: flex;
  min-width: 240px;
  max-width: 360px;
  flex: 1 1 260px;
  align-items: center;
  border: 1px solid #d1d5db;
  border-radius: 9px;
  padding: 0 14px;
  color: #94a3b8;
  background: #fff;
}

.users-search:focus-within {
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
}

.users-search input {
  width: 100%;
  min-width: 0;
  padding: 11px;
  border: 0;
  box-shadow: none;
  color: #334155;
  font-size: 0.875rem;
  outline: none;
}

.users-search input::-webkit-search-cancel-button {
  -webkit-appearance: none;
  appearance: none;
}

.users-search button {
  color: #94a3b8;
}

.users-toolbar__actions {
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 12px;
}

.users-toolbar__actions ::v-deep .tooltip-content {
  z-index: 20;
  max-width: none;
  white-space: nowrap;
}

.users-action-tooltip {
  position: relative;
  display: inline-flex;
}

.users-action-tooltip__content {
  position: absolute;
  top: calc(100% + 10px);
  z-index: 30;
  display: none;
  width: max-content;
  max-width: min(20rem, calc(100vw - 2rem));
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  background: #1f2937;
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.18);
  color: #fff;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.3;
  white-space: nowrap;
}

.users-action-tooltip--right .users-action-tooltip__content {
  right: 0;
}

.users-action-tooltip__content::before {
  position: absolute;
  right: 15px;
  bottom: 100%;
  border-right: 5px solid transparent;
  border-bottom: 5px solid #1f2937;
  border-left: 5px solid transparent;
  content: '';
}

.users-action-tooltip:hover .users-action-tooltip__content,
.users-action-tooltip:focus-within .users-action-tooltip__content {
  display: block;
}

.users-add-button,
.users-icon-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: #2563eb;
  color: #fff;
  transition: background 150ms ease, transform 150ms ease;
}

.users-add-button:hover,
.users-icon-button:hover {
  background: #1d4ed8;
}

.users-add-button {
  gap: 0.5rem;
  min-height: 44px;
  padding: 12px 18px;
  font-size: 0.875rem;
  font-weight: 400;
}

.users-icon-button {
  width: 44px;
  height: 44px;
}

.users-icon-button:disabled {
  background: #93c5fd;
  cursor: wait;
}

.users-table-scroll {
  contain: paint;
  min-height: 0;
  flex: 1 1 auto;
  overflow: auto;
}

.users-table {
  width: 100%;
  min-width: 1100px;
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;
}

.users-table--one-optional-column {
  min-width: 1250px;
}

.users-table--two-optional-columns {
  min-width: 1400px;
}

.users-table th {
  position: sticky;
  top: 0;
  z-index: 2;
  padding: 12px 14px;
  background: #f9fafb;
  color: #4b5563;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-align: left;
  text-transform: uppercase;
  vertical-align: middle;
}

.users-table th.users-column--menu-open {
  z-index: 5;
}

.users-table td {
  box-sizing: border-box;
  height: 52px;
  padding: 8px 14px;
  border-top: 1px solid #e5e7eb;
  color: #111827;
  font-size: 14px;
  line-height: 1.25rem;
  vertical-align: middle;
}

.users-table tbody tr {
  height: 52px;
}

.users-table tbody tr:hover {
  background: #f9fafb;
}

.users-row--editable {
  cursor: pointer;
}

.users-row--editable:focus-visible {
  outline: 2px solid #2563eb;
  outline-offset: -2px;
}

.users-column-trigger {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 3px 0;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  letter-spacing: inherit;
  text-align: left;
  text-transform: inherit;
}

.users-column-heading {
  position: relative;
  display: flex;
  min-height: 24px;
  align-items: center;
}

.users-column-trigger:hover,
.users-column-trigger:focus-visible {
  color: #2563eb;
}

.users-column-trigger:focus-visible {
  outline: 2px solid #2563eb;
  outline-offset: 3px;
}

.users-column-trigger__filter-dot {
  width: 8px;
  height: 8px;
  margin-left: -4px;
  border: 2px solid #f9fafb;
  border-radius: 999px;
  background: #2563eb;
}

.users-column-popover {
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  z-index: 10;
  min-width: 230px;
  padding: 12px;
  border: 1px solid #dbe1ea;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.18);
  color: #111827;
  font-size: 12px;
  font-weight: 400;
  letter-spacing: normal;
  text-transform: none;
}

.users-column--status .users-column-popover {
  right: 0;
  left: auto;
  min-width: 250px;
}

.users-column-popover__group {
  display: flex;
  flex-direction: column;
  gap: 7px;
  margin: 0 0 12px;
}

.users-column-popover__group:last-child {
  margin-bottom: 0;
}

.users-column-popover__label {
  color: #64748b;
  font-size: 11px;
  font-weight: 700;
}

.users-column-popover__actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
}

.users-column-popover button,
.users-column-popover select {
  min-height: 34px;
  padding: 7px 9px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #fff;
  color: #111827;
  font: inherit;
  text-transform: none;
}

.users-column-popover select {
  width: 100%;
}

.users-column-popover__actions button {
  cursor: pointer;
}

.users-column-popover__actions button:hover,
.users-column-popover__actions button.is-active {
  border-color: #2563eb;
  background: #eff6ff;
  color: #1d4ed8;
}

.users-column-popover__reset {
  width: 100%;
  color: #2563eb !important;
  cursor: pointer;
}

.users-column-popover__reset:hover {
  border-color: #93c5fd;
  background: #eff6ff;
}

.users-column--selection {
  width: 54px;
  text-align: center !important;
}

.users-column--selection input {
  width: 16px;
  height: 16px;
  accent-color: #2563eb;
  cursor: pointer;
}

.users-column--selection input:disabled {
  cursor: not-allowed;
}

.users-column--role {
  width: 175px;
}

.users-column--email {
  width: 245px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.users-column--groups {
  width: 190px;
}

.users-table td.users-column--groups {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.users-column--date {
  width: 195px;
  white-space: nowrap;
}

.users-column--status {
  width: 110px;
  text-align: center !important;
}

.users-column--status .users-column-heading,
.users-column--status .users-column-trigger {
  justify-content: center;
}

.users-role {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  padding: 0;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;
}

.users-status {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}

.users-status--downloaded {
  color: #16a34a;
}

.users-status--not-downloaded {
  color: #ef4444;
}

.users-table-state {
  height: 240px !important;
  color: #4b5563 !important;
  text-align: center;
}

.users-table-state i {
  margin-right: 8px;
}

.users-table-state--error {
  color: #b91c1c !important;
}

.users-table-state--error button {
  margin-left: 12px;
  color: #2563eb;
  font-weight: 600;
}

.users-table-footer {
  display: flex;
  min-height: 4rem;
  align-items: center;
  justify-content: space-between;
  flex: 0 0 auto;
  gap: 1rem;
  padding: 12px 24px;
  border-top: 1px solid #e5e7eb;
  background: #fff;
  color: #4b5563;
  font-size: 12px;
}

.users-table-footer__selection,
.users-pagination {
  display: flex;
  align-items: center;
  gap: 10px;
}

.users-delete-button {
  display: inline-flex;
  width: 36px;
  height: 36px;
  align-items: center;
  justify-content: center;
  border-radius: 9px;
  background: #fee2e2;
  color: #b91c1c;
  font-size: 0.8rem;
  font-weight: 700;
}

.users-delete-button:not(:disabled):hover {
  background: #fecaca;
}

.users-delete-button:disabled {
  background: #f1f5f9;
  color: #94a3b8;
  cursor: not-allowed;
}

.users-selection-count {
  color: #64748b;
  font-size: 12px;
  font-weight: 600;
}

.users-pagination {
  justify-content: flex-end;
}

.users-pagination button {
  display: inline-flex;
  width: 32px;
  height: 32px;
  align-items: center;
  justify-content: center;
  border: 1px solid #d1d5db;
  border-radius: 7px;
  background: #fff;
  color: #4b5563;
}

.users-pagination button:hover:not(:disabled) {
  border-color: #93c5fd;
  color: #2563eb;
}

.users-pagination button:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}

.users-pagination button:focus-visible,
.users-table-state--error button:focus-visible,
.users-column--selection input:focus-visible {
  outline: 2px solid #2563eb;
  outline-offset: 2px;
}

@media (max-width: 1200px) {
  .users-metrics {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 800px) {
  .users-page {
    height: auto;
    min-height: calc(100vh - 64px);
    overflow: visible;
  }

  .users-page__content {
    height: auto;
  }

  .users-metrics {
    grid-template-columns: 1fr;
  }

  .users-list-card {
    min-height: 32rem;
  }

  .users-toolbar {
    align-items: stretch;
    flex-direction: column;
  }

  .users-search {
    min-width: 0;
    max-width: none;
    flex-basis: auto;
  }

  .users-toolbar__actions {
    flex-wrap: wrap;
  }

  .users-add-button {
    flex: 1 1 auto;
  }

  .users-table-footer {
    align-items: stretch;
    flex-direction: column;
  }

  .users-pagination {
    justify-content: space-between;
  }
}
</style>
